import type { MovementError } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { MovementUpdatePayload } from "@services/movement-services/types";
import { updateMovementById } from "@services/movementServicesApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

export type UpdateMovementVariables = Parameters<typeof updateMovementById>[0];

export const useUpdateMovement = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: (variables: UpdateMovementVariables) => void;
  handleError: (error: MovementError, variables: UpdateMovementVariables) => void;
}) => {
  const updateMovementMutation = useMutation({
    mutationFn: (variables: UpdateMovementVariables) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "MovementService",
          action: "update_movement_service",
          entityType: "movement_service",
        },
        (transactionId) =>
          updateMovementById({ ...variables, ...(transactionId ? { transactionId } : {}) }),
      ),
    onSuccess: (_data, variables) => handleSuccess(variables),
    onError: (error: MovementError, variables) => handleError(error, variables),
  });

  const updateMovement = async ({
    id,
    service,
    payload,
  }: {
    id: string;
    service: string;
    payload: MovementUpdatePayload;
  }) => {
    updateMovementMutation.mutate({
      id,
      service,
      payload,
    });
  };

  return {
    updateMovement,
    isPending: updateMovementMutation.isPending,
  };
};
